import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FaPlay } from 'react-icons/fa';
import { useLanguage } from '../../hooks/useLanguage';
import { getTranslation } from '../../translations/presentation';

// Animações de entrada
const digitalReveal = keyframes`
  0% {
    opacity: 0;
    clip-path: polygon(0 0, 0 0, 0 100%, 0 100%);
  }
  20% {
    opacity: 0.2;
    clip-path: polygon(0 0, 10% 0, 10% 100%, 0 100%);
  }
  40% {
    opacity: 0.4;
    clip-path: polygon(0 0, 30% 0, 30% 100%, 0 100%);
  }
  60% {
    opacity: 0.6;
    clip-path: polygon(0 0, 70% 0, 70% 100%, 0 100%);
  }
  80% {
    opacity: 0.8;
    clip-path: polygon(0 0, 90% 0, 90% 100%, 0 100%);
  }
  100% {
    opacity: 1;
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
  }
`;

const gridFormation = keyframes`
  0% {
    opacity: 0;
    background-size: 0px 0px;
    background-position: 0% 0%;
  }
  30% {
    opacity: 0.3;
    background-size: 50px 50px;
    background-position: 10% 10%;
  }
  60% {
    opacity: 0.6;
    background-size: 100px 100px;
    background-position: 20% 20%;
  }
  100% {
    opacity: 1;
    background-size: 40px 40px;
    background-position: 0% 0%;
  }
`;

const pulseGlow = keyframes`
  0% { box-shadow: 0 0 5px rgba(100, 182, 255, 0.3); }
  50% { box-shadow: 0 0 25px rgba(100, 182, 255, 0.7); }
  100% { box-shadow: 0 0 5px rgba(100, 182, 255, 0.3); }
`;

const scanLine = keyframes`
  0% {
    top: -10%;
    opacity: 0;
  }
  10% {
    opacity: 0.8;
  }
  80% {
    opacity: 0.8;
  }
  100% {
    top: 110%;
    opacity: 0;
  }
`;

const dataFlowAnimation = keyframes`
  0% {
    background-position: 0% 0%;
  }
  100% {
    background-position: 100% 100%;
  }
`;

const glitchEffect = keyframes`
  0% {
    text-shadow: 0.05em 0 0 rgba(255, 0, 0, 0.75), -0.05em -0.025em 0 rgba(0, 255, 0, 0.75), 0.025em 0.05em 0 rgba(0, 0, 255, 0.75);
  }
  14% {
    text-shadow: 0.05em 0 0 rgba(255, 0, 0, 0.75), -0.05em -0.025em 0 rgba(0, 255, 0, 0.75), 0.025em 0.05em 0 rgba(0, 0, 255, 0.75);
  }
  15% {
    text-shadow: -0.05em -0.025em 0 rgba(255, 0, 0, 0.75), 0.025em 0.025em 0 rgba(0, 255, 0, 0.75), -0.05em -0.05em 0 rgba(0, 0, 255, 0.75);
  }
  49% {
    text-shadow: -0.05em -0.025em 0 rgba(255, 0, 0, 0.75), 0.025em 0.025em 0 rgba(0, 255, 0, 0.75), -0.05em -0.05em 0 rgba(0, 0, 255, 0.75);
  }
  50% {
    text-shadow: 0.025em 0.05em 0 rgba(255, 0, 0, 0.75), 0.05em 0 0 rgba(0, 255, 0, 0.75), 0 -0.05em 0 rgba(0, 0, 255, 0.75);
  }
  99% {
    text-shadow: 0.025em 0.05em 0 rgba(255, 0, 0, 0.75), 0.05em 0 0 rgba(0, 255, 0, 0.75), 0 -0.05em 0 rgba(0, 0, 255, 0.75);
  }
  100% {
    text-shadow: -0.025em 0 0 rgba(255, 0, 0, 0.75), -0.025em -0.025em 0 rgba(0, 255, 0, 0.75), -0.025em -0.05em 0 rgba(0, 0, 255, 0.75);
  }
`;

const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const borderPulse = keyframes`
  0% { border-color: rgba(100, 182, 255, 0.2); }
  50% { border-color: rgba(100, 182, 255, 0.8); }
  100% { border-color: rgba(100, 182, 255, 0.2); }
`;

const rotateGear = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const cornerPulse = keyframes`
  0% { opacity: 0; height: 0; width: 0; }
  50% { opacity: 1; height: 20px; width: 20px; }
  100% { opacity: 0; height: 0; width: 0; }
`;

const SlideWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 20px;
  overflow: hidden;
  position: relative;
  background: transparent;
  
  &::after {
    content: '';
    position: absolute;
    top: -10%;
    left: 0;
    width: 100%;
    height: 10%;
    background: linear-gradient(to bottom, 
      rgba(100, 182, 255, 0) 0%,
      rgba(100, 182, 255, 0.2) 50%,
      rgba(100, 182, 255, 0) 100%);
    animation: ${scanLine} 4s ease-in-out forwards;
    animation-delay: 1s;
    pointer-events: none;
  }
`;

const DataStream = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background: 
    radial-gradient(1px 1px at ${props => props.x1}% ${props => props.y1}%, rgba(100, 182, 255, 0.3) 2px, transparent 0),
    radial-gradient(1px 1px at ${props => props.x2}% ${props => props.y2}%, rgba(0, 240, 255, 0.3) 2px, transparent 0),
    radial-gradient(1px 1px at ${props => props.x3}% ${props => props.y3}%, rgba(150, 240, 255, 0.3) 2px, transparent 0);
  background-size: 600px 600px;
  opacity: 0;
  animation: ${dataFlowAnimation} 15s linear infinite, ${props => css`
    ${keyframes`
      0% { opacity: 0; }
      30% { opacity: 0.2; }
      70% { opacity: 0.4; }
      100% { opacity: 0.2; }
    `} ${props.delay}s ease-out forwards
  `};
  pointer-events: none;
`;

const SlideTitle = styled.h2`
  font-family: 'Orbitron', sans-serif;
  color: #64B6FF;
  margin-bottom: 30px;
  text-align: center;
  letter-spacing: 2px;
  font-size: 1.8rem;
  text-transform: uppercase;
  position: absolute;
  top: -100px;
  left: 0;
  right: 0;
  text-shadow: 0 0 10px rgba(100, 182, 255, 0.5);
  animation: ${keyframes`
    0% { top: -100px; opacity: 0; }
    40% { top: -100px; opacity: 0; }
    70% { top: 20px; opacity: 0.7; }
    90% { top: 35px; opacity: 0.9; }
    100% { top: 30px; opacity: 1; }
  `} 3s cubic-bezier(0.19, 1, 0.22, 1) forwards;
  
  &::before {
    content: '//';
    color: rgba(100, 182, 255, 0.5);
    margin-right: 10px;
  }
`;

const TechCorner = styled.div`
  position: absolute;
  width: 20px;
  height: 20px;
  border-style: solid;
  border-color: rgba(100, 182, 255, 0.8);
  border-width: ${props => props.borderStyle};
  pointer-events: none;
  opacity: 0;
  ${props => props.position};
  animation: ${cornerPulse} 4s infinite;
  animation-delay: ${props => props.delay}s;
`;

const DataFlowLine = styled.div`
  position: absolute;
  background: linear-gradient(to ${props => props.direction}, transparent, rgba(100, 182, 255, 0.3), transparent);
  ${props => props.orientation === 'horizontal' ? 'height: 1px; width: 30%;' : 'width: 1px; height: 30%;'}
  ${props => props.position};
  opacity: 0;
  animation: ${keyframes`
    0% { opacity: 0; ${props => props.fromPos} }
    20% { opacity: 0.7; }
    80% { opacity: 0.7; }
    100% { opacity: 0; ${props => props.toPos} }
  `} 3s linear forwards;
  animation-delay: ${props => props.delay}s;
  pointer-events: none;
`;

const TechElement = styled.div`
  position: absolute;
  font-family: 'Courier New', monospace;
  font-size: 0.75rem;
  color: rgba(100, 182, 255, 0.8);
  padding: 5px 10px;
  z-index: 5;
  opacity: 0;
  transform: translateY(-20px);
  text-shadow: 0 0 5px rgba(100, 182, 255, 0.5);
  animation: ${keyframes`
    0% { transform: translateY(-20px); opacity: 0; }
    30% { transform: translateY(-5px); opacity: 0.3; }
    50% { transform: translateY(10px); opacity: 0.5; }
    70% { top: 20px; opacity: 0.7; }
    90% { top: 35px; opacity: 0.9; }
    100% { top: 30px; opacity: 1; }
  `} 3s cubic-bezier(0.19, 1, 0.22, 1) forwards;
  
  &::before {
    content: '//';
    color: rgba(100, 182, 255, 0.5);
    margin-right: 10px;
    animation: ${glitchEffect} 5s infinite;
    animation-delay: 3s;
  }
  
  &::after {
    content: '.platform';
    color: rgba(100, 182, 255, 0.5);
    margin-left: 5px;
    font-size: 0.9em;
    animation: ${glitchEffect} 5s infinite;
    animation-delay: 3.5s;
  }
`;

const VideoContainer = styled.div`
  width: 65%;
  aspect-ratio: 16/9;
  background: rgba(23, 29, 58, 0.95);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  border: 1px solid rgba(100, 182, 255, 0.2);
  animation: ${keyframes`
    0% { transform: perspective(1000px) rotateX(90deg); opacity: 0; filter: brightness(0); }
    30% { transform: perspective(1000px) rotateX(20deg); opacity: 0.3; filter: brightness(0.3) blur(5px); }
    100% { transform: perspective(1000px) rotateX(0deg); opacity: 1; filter: brightness(1) blur(0); }
  `} 1.5s cubic-bezier(0.23, 1, 0.32, 1) forwards, ${borderPulse} 4s infinite;
  animation-delay: 2s, 3.5s;
  transform-origin: center center;
  opacity: 0;
  z-index: 10;
  
  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 8px;
    border: 1px solid transparent;
    box-sizing: border-box;
    animation: ${keyframes`
      0% { transform: scale(1); border-color: rgba(100, 182, 255, 0); }
      50% { transform: scale(1.02); border-color: rgba(100, 182, 255, 0.3); }
      100% { transform: scale(1); border-color: rgba(100, 182, 255, 0); }
    `} 4s infinite;
    pointer-events: none;
  }
  
  &::before {
    animation-delay: 0s;
  }
  
  &::after {
    animation-delay: 2s;
  }
`;

const PlayButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  opacity: 0;
  transform: scale(0.5);
  animation: ${keyframes`
    0% { opacity: 0; transform: scale(0.5); }
    70% { opacity: 0.7; transform: scale(1.1); }
    100% { opacity: 1; transform: scale(1); }
  `} 1s forwards;
  animation-delay: 3.5s;
  z-index: 20;
`;

const PlayButton = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.3);
  border: 2px solid #64B6FF;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 15px rgba(100, 182, 255, 0.5);
  transition: all 0.3s ease;
  animation: ${pulseGlow} 2s infinite;
  animation-delay: 4s;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 0 20px rgba(100, 182, 255, 0.7);
  }
  
  svg {
    color: #64B6FF;
    font-size: 24px;
    margin-left: 4px;
  }
`;

const VideoFrame = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 8px;
  animation: ${digitalReveal} 0.5s ease-in-out forwards;
`;

const Logo = styled.div`
  margin-bottom: 20px;
  opacity: 0;
  animation: ${keyframes`
    0% { opacity: 0; transform: translateY(-20px); }
    100% { opacity: 1; transform: translateY(0); }
  `} 1s forwards, ${floatAnimation} 3s ease-in-out infinite;
  animation-delay: 3s, 4s;
  
  img {
    width: 80px;
    height: auto;
    filter: drop-shadow(0 0 10px rgba(100, 182, 255, 0.5));
  }
`;

function openNativePlayerWindow(videoSrc) {
  const playerScript = `
    document.addEventListener('DOMContentLoaded', function() {
      const video = document.getElementById('fullscreen-video');
      if (video) {
        video.controls = true;
        
        video.play().catch(function(e) { 
          console.warn('Não foi possível reproduzir automaticamente:', e); 
        });
        
        window.addEventListener('unload', function() {
          try {
            if (window.opener && !window.opener.closed) {
              window.opener.postMessage({
                type: 'fullscreenClosed'
              }, '*');
            }
          } catch(e) {
            console.warn('Erro ao comunicar com janela principal:', e);
          }
        });
      }
    });
  `;

  const playerHTML = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>BIANCA - Por dentro da plataforma</title>
      <style>
        body, html { 
          margin: 0; 
          padding: 0; 
          width: 100%; 
          height: 100%; 
          overflow: hidden;
          background-color: #000;
        }
        #fullscreen-video {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      </style>
      <script>${playerScript}</script>
    </head>
    <body>
      <video id="fullscreen-video" src="${videoSrc}" autoplay controlsList="nodownload" disablePictureInPicture></video>
    </body>
    </html>
  `;

  const windowFeatures = "popup,fullscreen=yes,width=" + window.screen.width + ",height=" + window.screen.height;
  const playerWindow = window.open("", "videoPlayer", windowFeatures);
  
  if (playerWindow) {
    playerWindow.document.write(playerHTML);
    playerWindow.document.close();
    
    playerWindow.focus();
    
    return playerWindow;
  }
  
  return null;
}

const PresentationSlide21 = () => {
  const { lang } = useLanguage();
  const t = getTranslation('slide21', lang);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playerWindow, setPlayerWindow] = useState(null);
  const videoUrl = process.env.PUBLIC_URL + '/videos/bianca_demo.mp4';
  
  useEffect(() => {
    const handleMessage = (event) => {
      if (playerWindow && event.source === playerWindow) {
        const data = event.data;
        
        if (data && data.type === 'fullscreenClosed') {
          setIsPlaying(false);
          setPlayerWindow(null);
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [playerWindow]);
  
  useEffect(() => {
    if (playerWindow) {
      const checkWindow = setInterval(() => {
        if (playerWindow && playerWindow.closed) {
          setIsPlaying(false);
          setPlayerWindow(null);
          clearInterval(checkWindow);
        }
      }, 500);
      
      return () => {
        clearInterval(checkWindow);
      };
    }
  }, [playerWindow]);
  
  const handlePlayClick = () => {
    const videoPath = new URL(videoUrl, window.location.origin).href;
    
    const newWindow = openNativePlayerWindow(videoPath);
    
    if (newWindow) {
      setPlayerWindow(newWindow);
      setIsPlaying(true);
    } else {
      alert('Não foi possível abrir o modo de tela cheia. Verifique se os pop-ups estão permitidos.');
    }
  };
  
  return (
    <SlideWrapper>
      <DataStream x1="20" y1="30" x2="70" y2="50" x3="40" y3="80" delay="1.5" />
      <DataStream x1="50" y1="10" x2="30" y2="60" x3="80" y3="40" delay="2" />
      <DataStream x1="10" y1="70" x2="90" y2="20" x3="60" y3="90" delay="2.5" />
      
      <SlideTitle>{t.title}</SlideTitle>
      
      {/* Elementos decorativos tecnológicos */}
      <TechCorner 
        borderStyle="2px 0 0 2px" 
        position="top: 0; left: 0;" 
        delay="3"
      />
      <TechCorner 
        borderStyle="2px 2px 0 0" 
        position="top: 0; right: 0;" 
        delay="3.2"
      />
      <TechCorner 
        borderStyle="0 0 2px 2px" 
        position="bottom: 0; left: 0;" 
        delay="3.4"
      />
      <TechCorner 
        borderStyle="0 2px 2px 0" 
        position="bottom: 0; right: 0;" 
        delay="3.6"
      />
      
      <DataFlowLine 
        orientation="horizontal" 
        direction="right" 
        position="top: 10%; left: 0;" 
        fromPos="transform: translateX(-100%);" 
        toPos="transform: translateX(300%);" 
        delay="3"
      />
      <DataFlowLine 
        orientation="horizontal" 
        direction="left" 
        position="bottom: 10%; right: 0;" 
        fromPos="transform: translateX(100%);" 
        toPos="transform: translateX(-300%);" 
        delay="3.5"
      />
      <DataFlowLine 
        orientation="vertical" 
        direction="bottom" 
        position="top: 0; left: 20%;" 
        fromPos="transform: translateY(-100%);" 
        toPos="transform: translateY(300%);" 
        delay="4"
      />
      <DataFlowLine 
        orientation="vertical" 
        direction="top" 
        position="bottom: 0; right: 20%;" 
        fromPos="transform: translateY(100%);" 
        toPos="transform: translateY(-300%);" 
        delay="4.5"
      />
      
      <VideoContainer>
        <Logo>
          <img src="/favicon.png" alt="Logo Bianca" />
        </Logo>
        <PlayButtonContainer onClick={handlePlayClick}>
          <PlayButton>
            <FaPlay />
          </PlayButton>
        </PlayButtonContainer>
      </VideoContainer>
    </SlideWrapper>
  );
};

export default PresentationSlide21;
